// packages/app-shell/src/process/BpmnView.tsx
//
// Horizontal-BPMN renderer over @archlang/engine's BpmnLayout (plan
// 2026-07-07-process-scaffold-bpmn-diff P2). Pure SVG projection of the grid:
// tasks as rounded rects (owner chip above), gateways as diamonds with the
// BPMN 2.0 glyph set (×/+/◯/✳/event-pentagon), events as weighted circles
// with trigger/result glyphs (timer, conditional, error, cancel, link,
// terminate), frames behind with activity markers (loop ↻ / multi-instance ≡ /
// collapsed-subprocess ⊞), text annotations as open brackets, and the
// default-flow slash on `else` edges. Layout owns all positions; this
// component only scales grid → pixels.
//
// Scene-substrate unification (plan 2026-07-07-scene-process-render P5): all
// pixel constants and edge/canvas GEOMETRY come from @archlang/scene/process —
// the same source the headless SVG export renders from, so live and export
// cannot drift on layout. Paint stays Tailwind classes (theme-reactive by
// design; the export resolves its own fixed-theme tokens); glyph micro-paths
// (gateway/event/marker vocabulary) stay per-surface, pinned by the scene
// goldens on one side and this component's tests on the other.

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import type { BpmnEdge, BpmnLaneBand, BpmnLayout, BpmnNode, BpmnPoolBand } from "@archlang/engine";
import type { NodeId, StepLatency } from "@archlang/engine";
import { bandAtY, type BpmnLaneEditApi } from "./bpmnLaneEdit.js";
import type { StepEdgeStyle } from "./SwimlaneView.js";
import type { ProcessStepEditApi } from "./processStepEdit.js";
import { StepEditMenu } from "./StepEditMenu.js";
import { ConnectorInsert } from "./ConnectorInsert.js";
import { StepEditPrompt, type StepPromptSpec } from "./StepEditPrompt.js";
import { stepEditMenuItems } from "./stepEditMenuItems.js";
import { ProcessMinimap } from "./ProcessMinimap.js";
import { useStepDrag } from "./useStepDrag.js";
import { resolveHorizontalDrop, type BpmnDragNode } from "./bpmnDragResolve.js";
import {
  CW,
  CH,
  PAD,
  LANE_RAIL_CELL_W,
  LANE_SEPARATOR_STROKE,
  LANE_LABEL_SIZE,
  LANE_LABEL_CHAR_H,
  LANE_LABEL_PAD,
  POOL_BORDER_STROKE,
  POOL_BORDER_RADIUS,
  TASK_W,
  TASK_H,
  GATE_R,
  EVENT_R,
  SYSTEM_W,
  SYSTEM_H,
  SYSTEM_CAP_RY,
  SYSTEM_STROKE,
  SYSTEM_LABEL_DY,
  SYSTEM_FILL_ALPHA,
  ENGINE_BADGE_W,
  ENGINE_BADGE_H,
  ENGINE_BADGE_R,
  ENGINE_BADGE_DX,
  ENGINE_BADGE_DY,
  ENGINE_STROKE,
  ENGINE_FILL_ALPHA,
  FRAME_INSET_X,
  FRAME_INSET_Y,
  FRAME_INSET_W,
  FRAME_INSET_H,
  FRAME_RADIUS,
  FRAME_DASH_DIST,
  FRAME_DASH_DEFAULT,
  gridCx,
  gridCy,
  reentryLift,
  bpmnEdgeGeom,
  defaultSlashD,
  fitLabel,
  FONT_TITLE,
  FONT_LABEL,
  FONT_SMALL,
  FONT_WEIGHT_MEDIUM,
  LABEL_W_TASK,
  LABEL_W_OWNER,
  LABEL_W_GATEWAY,
  LABEL_W_EVENT,
  LABEL_W_ANNOTATION,
  LABEL_W_FRAME,
  LABEL_W_EDGE,
  LABEL_W_DIFF_DETAIL,
  LABEL_W_SYSTEM,
  LABEL_W_ENGINE,
  defaultProcessTokens,
} from "@archlang/scene/process";

export interface BpmnViewProps {
  readonly layout: BpmnLayout;
  readonly activeStepIndex: number | null;
  readonly onSelectStepIndex: (i: number | null) => void;
  readonly onSubprocessToggle: (nodeId: NodeId, expanded: boolean) => void;
  readonly onSelectModule: (qualified: string) => void;
  /** Clicking a pickable split out-edge label (if/switch/try alternatives)
   *  selects that branch in the walker state. */
  readonly onBranchSelect?: (nodeId: NodeId, label: string) => void;
  /**
   * Owner-path → colour from the unified node-coloring layer (the board's
   * "colour by" picker: type by default, or a label key). Colours the task
   * stroke + owner chip; diff/compensation accents take precedence.
   */
  readonly ownerColor?: (path: readonly string[]) => string | undefined;
  /** WYSIWYG lane/pool rail editing (S-lane-edit). Present ⇒ the rail renders
   *  rename/remove/reorder/add affordances on editable bands; absent ⇒ the rail
   *  is read-only (every other surface: process tab, diff, export). */
  readonly edit?: BpmnLaneEditApi;
  /** Per-step edge styling (compensation / dashed / interface colour) — parity
   *  with the flow/sequence surface; an extra stroke source on task nodes. */
  readonly resolveStepEdgeStyle?: (stepIndex: number) => StepEdgeStyle | undefined;
  /** v0.10.2 per-step latencies keyed by walk stepIndex — the own-latency badge
   *  under each task node (parity with the sequence per-hop labels). */
  readonly stepLatencies?: ReadonlyMap<number, StepLatency>;
  /** WYSIWYG step editing — the ⋯ affordance on each task node. Read-only when
   *  absent / `canEdit` false. */
  readonly stepEdit?: ProcessStepEditApi;
  /** Live process-conformance: walk stepIndexes observed to run OUT OF ORDER vs
   *  the model. Task/event nodes for these steps carry a violation ring + ⇄
   *  marker — parity with the flow/sequence swimlane (see swimlaneCells). */
  readonly reorderedSteps?: ReadonlySet<number>;
}

// Grid → pixel centres, shared with the headless composer.
const cx = gridCx;
const cy = gridCy;

// Single-line label ellipsis shared with the headless composer: the SAME
// pixel budgets, measured with the SAME scene metrics, so live and export cut
// at the same glyph (and a long Cyrillic name never overflows its box).
const PROCESS_FONT_FAMILY = defaultProcessTokens().fontFamily;
const fit = (s: string, maxWidth: number, size: number, weight?: number): string =>
  fitLabel(s, maxWidth, size, PROCESS_FONT_FAMILY, weight);

/** Diff accent for a tagged node/frame: stroke override + ghost styling.
 *  (Plan P4 — union scaffolds arrive with `diff` tags; plain layouts don't.) */
function diffAccent(diff?: { status: string; detail?: string }): {
  stroke: string | null;
  dash?: string;
  ghost?: boolean;
} {
  if (!diff) return { stroke: null };
  if (diff.status === "added") return { stroke: "stroke-emerald-400" };
  if (diff.status === "removed") return { stroke: "stroke-rose-400", dash: "5 4", ghost: true };
  if (diff.status === "changed") return { stroke: "stroke-amber-400" };
  return { stroke: null };
}

function DiffChip({ x, y, diff }: { x: number; y: number; diff?: { status: string; detail?: string } }) {
  if (!diff || diff.status !== "changed" || !diff.detail) return null;
  return (
    <>
      <title>{diff.detail}</title>
      <text x={x} y={y} textAnchor="middle" className="fill-amber-400 text-[9px]" data-diff-chip>
        {fit(diff.detail, LABEL_W_DIFF_DETAIL, FONT_SMALL)}
      </text>
    </>
  );
}

const EDGE_CLASS: Record<BpmnEdge["style"], string> = {
  seq: "stroke-slate-400",
  comp: "stroke-amber-400/80",
  drop: "stroke-rose-400/70",
  reentry: "stroke-purple-400/80",
  assoc: "stroke-slate-500",
};
const EDGE_DASH: Record<BpmnEdge["style"], string | undefined> = {
  seq: undefined,
  comp: "6 4",
  drop: "4 4",
  reentry: "6 4",
  assoc: "2 4",
};

export function BpmnView(props: BpmnViewProps) {
  const { layout } = props;
  const svgRef = useRef<SVGSVGElement>(null);
  // Sticky lane rail: the scroll container's vertical offset, so a pinned rail
  // overlay tracks the body's vertical scroll while staying fixed at the left
  // edge horizontally (a 27-column ribbon scrolls the labels off-screen
  // otherwise). scrollLeft gates rendering the overlay at all — at rest (x=0)
  // it sits pixel-identical over the body rail, so it only needs to appear
  // once the body rail has scrolled away.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Viewport (scroll container client) size for the overview minimap's rect.
  const [viewport, setViewport] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => setViewport({ w: el.clientWidth, h: el.clientHeight }));
    obs.observe(el);
    setViewport({ w: el.clientWidth, h: el.clientHeight });
    return () => obs.disconnect();
  }, []);
  // Step→lane drag: the stepIndex under an active owner-reassign drag. Only
  // armed when the host supplies `edit.reassignStep` and there are lane bands.
  const [dragStep, setDragStep] = useState<number | null>(null);
  const reassign = props.edit?.reassignStep;
  const canDragSteps = !!reassign && layout.lanes.length > 0;
  // Lane rail (swimlanes & pools) — same cell metrics as the headless
  // composer's `composeLaneRail`; grid content shifts right by the rail width.
  const lanes = layout.lanes;
  const spineW = layout.pools.length > 0 ? LANE_RAIL_CELL_W : 0;
  const labelW = lanes.some((l) => l.title !== null) ? LANE_RAIL_CELL_W : 0;
  const railW = lanes.length > 0 ? spineW + labelW : 0;
  const w = railW + PAD * 2 + layout.width * CW;
  // Reserve headroom for `go` back-arcs — their apex rises above the top
  // track and would clip at the viewport otherwise.
  const byId = new Map<string, BpmnNode>(layout.nodes.map((n) => [n.id, n]));
  let headroom = 0;
  for (const e of layout.edges) {
    if (e.style !== "reentry") continue;
    const a = byId.get(e.from);
    const b = byId.get(e.to);
    if (a && b) headroom = Math.max(headroom, reentryLift(Math.abs(a.x - b.x)));
  }
  const h = PAD * 2 + layout.height * CH + headroom;

  const startDrag = (stepIndex: number, e: ReactPointerEvent) => {
    e.stopPropagation();
    setDragStep(stepIndex);
  };
  // Resolve a drop: the lane band under the pointer must project a single owner
  // module (the foreman guard); the reassign API itself no-ops a same-owner or
  // non-plain step. Left content shifts right by `railW`, but bands span the
  // full width and are keyed by y only — no x adjust needed.
  const finishDrag = (clientY: number) => {
    if (dragStep !== null && reassign) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const band = bandAtY(layout.lanes, clientY - rect.top - headroom);
        if (band?.ownerModule) reassign(dragStep, band.ownerModule);
      }
    }
    if (dragStep !== null) setDragStep(null);
  };

  // Edit-mode connector insert: for a sequence edge, one "+" per gap. Prefer
  // "after the source step"; at a leading gap (start/gateway → first step) fall
  // back to "before the target step". Non-step endpoints (both events/gateways)
  // get no affordance. Returns the committed inserter, or null when inapplicable.
  const stepEdit = props.stepEdit;
  const insertOnEdge = (e: BpmnEdge): ((text: string) => boolean) | null => {
    if (!stepEdit || e.style !== "seq") return null;
    const src = byId.get(e.from);
    if (src && "stepIndex" in src && src.stepIndex !== null && src.stepIndex !== undefined) {
      return (text) => stepEdit.addStepAfter(e.from as NodeId, text);
    }
    const dst = byId.get(e.to);
    if (dst && "stepIndex" in dst && dst.stepIndex !== null && dst.stepIndex !== undefined) {
      return (text) => stepEdit.insertBefore(e.to as NodeId, text);
    }
    return null;
  };
  // The right-click edit ops' text prompt (add / retype / reassign) — one
  // floating input per surface, opened by a menu item at the pointer.
  const [stepPrompt, setStepPrompt] = useState<StepPromptSpec | null>(null);

  // Horizontal drag reorder (P5 #35 — BPMN analog of the flow/sequence handle
  // drag): the draggable steps are the REAL call task nodes as scene-geometry
  // bands (grid columns → pixel via cx/TASK_W — NO DOM-measured layout).
  // `toLocalX` projects the pointer into that band space through the svg's own
  // client rect (pointer hit-testing, not a layout decision), subtracting the
  // left rail so it lines up with the grid-space node bands. The drop commits
  // via `moveStep`. Reorder-only: BPMN shows `each`/`do`/`try` frames, not the
  // empty gateway blocks the flow view offers as distinct drop-into zones, so
  // there's no reparent-into-block target on this surface (parity kept on the
  // reorder axis).
  const canDragEdit = !!stepEdit?.canEdit;
  const dragNodes = useCallback((): BpmnDragNode[] => {
    if (!canDragEdit) return [];
    const out: BpmnDragNode[] = [];
    for (const n of layout.nodes) {
      // Real call steps only: a collapsed `do` (stepIndex null) is not a leaf
      // reorder target, and a compensation step lives off the forward spine.
      if (n.kind !== "task" || n.stepIndex === null || n.stepIndex === undefined) continue;
      if (n.subprocess || n.compensation) continue;
      out.push({ nodeId: n.id, left: cx(n.x) - TASK_W / 2, width: TASK_W });
    }
    return out;
  }, [canDragEdit, layout]);

  const toLocalX = useCallback(
    (clientX: number): number => {
      const rect = svgRef.current?.getBoundingClientRect();
      // jsdom yields a zero rect → identity projection (the pointer maps straight
      // onto the scene band space, like SwimlaneView's toLocalX). `railW` shifts
      // the grid content right; the node bands are in grid space, so subtract it.
      if (!rect || rect.width === 0) return clientX - railW;
      return (clientX - rect.left) * (w / rect.width) - railW;
    },
    [w, railW],
  );

  const drag = useStepDrag({
    resolve: (clientX, _clientY, dragged) => {
      const v = resolveHorizontalDrop(dragNodes(), toLocalX(clientX), dragged);
      return v ? { kind: "reorder", anchorNodeId: v.anchorNodeId, position: v.position } : null;
    },
    commit: (dragged, t) => {
      if (t.kind === "reorder") {
        stepEdit?.moveStep(dragged as unknown as NodeId, t.anchorNodeId as unknown as NodeId, t.position);
      }
    },
  });

  // Drop indicator: a vertical insertion line at the target anchor's leading /
  // trailing edge (the horizontal analog of the flow view's horizontal line).
  const dropIndicator = useMemo((): { x: number } | null => {
    const t = drag.target;
    if (!t || t.kind !== "reorder") return null;
    const anchor = dragNodes().find((n) => n.nodeId === t.anchorNodeId);
    if (!anchor) return null;
    return { x: t.position === "before" ? anchor.left : anchor.left + anchor.width };
  }, [drag.target, dragNodes]);

  return (
    <div className="relative h-full">
    <div
      ref={scrollRef}
      className="h-full overflow-auto bg-slate-950"
      data-testid="bpmn-view"
      onScroll={
        railW > 0
          ? (e) => setScroll({ top: e.currentTarget.scrollTop, left: e.currentTarget.scrollLeft })
          : undefined
      }
    >
      <svg
        ref={svgRef}
        width={w}
        height={h}
        className="group/bpmn block"
        role="img"
        aria-label="BPMN process diagram"
        data-headroom={headroom || undefined}
        data-drag-step={dragStep ?? undefined}
        onPointerUp={dragStep !== null ? (e) => finishDrag(e.clientY) : undefined}
        onPointerLeave={dragStep !== null ? () => setDragStep(null) : undefined}
      >
        <g transform={`translate(0, ${headroom})`}>
        {/* The rail shows whenever there ARE lanes, OR when container editing is
            enabled with none yet — so the "+ lane / + pool" footer is reachable
            to add the first band (the process tab's editable-view tier). */}
        {(lanes.length > 0 || (!!props.edit && !props.edit.reassignOnly)) && (
          <LaneRail
            lanes={lanes}
            pools={layout.pools}
            spineW={spineW}
            labelW={labelW}
            totalW={w}
            onSelectModule={props.onSelectModule}
            edit={props.edit}
          />
        )}
        <g transform={railW ? `translate(${railW}, 0)` : undefined}>
        <defs>
          <marker id="bpmn-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" className="fill-slate-400" />
          </marker>
          <marker id="bpmn-arrow-open" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10" fill="none" strokeWidth="1.5" className="stroke-slate-400" />
          </marker>
        </defs>

        {/* Drop indicator — a vertical insertion line at the resolved anchor's
            leading/trailing edge while a horizontal drag is live (P5 #35). */}
        {dropIndicator && (
          <line
            x1={dropIndicator.x}
            x2={dropIndicator.x}
            y1={0}
            y2={PAD * 2 + layout.height * CH}
            stroke="#38bdf8"
            strokeWidth={2.5}
            strokeDasharray="7 3"
            data-drop-line=""
            pointerEvents="none"
          />
        )}

        {/* Frames (behind everything). */}
        {layout.frames.map((f) => {
          const x = PAD + f.x0 * CW + FRAME_INSET_X;
          const y = PAD + f.y0 * CH + FRAME_INSET_Y;
          const fw = (f.x1 - f.x0 + 1) * CW - FRAME_INSET_W;
          const fh = (f.y1 - f.y0 + 1) * CH - FRAME_INSET_H;
          const dash = f.kind === "dist" ? FRAME_DASH_DIST : f.kind === "do" ? undefined : FRAME_DASH_DEFAULT;
          const accent = diffAccent(f.diff);
          const oc =
            !accent.stroke && f.owner && !f.owner.isActor
              ? props.ownerColor?.(f.owner.path)
              : undefined;
          return (
            <g
              key={f.id}
              data-frame-kind={f.kind}
              data-frame-id={f.id}
              data-frame-marker={f.marker}
              data-diff={f.diff?.status}
              data-owner-colored={oc ? true : undefined}
              opacity={accent.ghost ? 0.55 : undefined}
            >
              <rect
                x={x}
                y={y}
                width={fw}
                height={fh}
                rx={FRAME_RADIUS}
                fill="none"
                strokeDasharray={accent.dash ?? dash}
                style={oc ? { stroke: oc } : undefined}
                className={accent.stroke ?? "stroke-slate-600"}
              />
              {f.kind === "do" ? (
                // Expanded subprocess: the header is the collapse handle.
                <g
                  data-frame-collapse={f.id}
                  className="cursor-pointer"
                  onClick={() => props.onSubprocessToggle(f.id, false)}
                >
                  <text x={x + 8} y={y + 14} className="fill-slate-400 text-[10px] font-medium hover:fill-slate-200">
                    ⊟ {fit(f.label, LABEL_W_FRAME - 14, FONT_LABEL, FONT_WEIGHT_MEDIUM)}
                  </text>
                </g>
              ) : (
                <text x={x + 8} y={y + 14} className="fill-slate-500 text-[10px] font-medium">
                  {fit(f.label, LABEL_W_FRAME, FONT_LABEL, FONT_WEIGHT_MEDIUM)}
                </text>
              )}
              {f.kind === "each" && <ActivityMarker x={x + fw / 2} y={y + fh - 10} marker={f.marker ?? "loop"} />}
            </g>
          );
        })}

        {/* Edges. */}
        {layout.edges.map((e, i) => {
          const g = bpmnEdgeGeom(layout, e);
          if (!g) return null;
          const ins = insertOnEdge(e);
          const marker = e.style === "assoc" ? undefined : e.style === "seq" ? "url(#bpmn-arrow)" : "url(#bpmn-arrow-open)";
          // Diff tint: engine-derived (`BpmnEdge.diff`, endpoint rule — a seq
          // edge between two same-status added/removed nodes belongs to that
          // change: an inserted chain's internal edges are green; a removed
          // run's are ghost-dashed). Shared with the headless export.
          const tint = e.diff?.status === "added" || e.diff?.status === "removed" ? e.diff.status : undefined;
          const cls =
            tint === "added"
              ? "stroke-emerald-400/80"
              : tint === "removed"
                ? "stroke-rose-400/60"
                : EDGE_CLASS[e.style];
          const dash = tint === "removed" ? "5 4" : EDGE_DASH[e.style];
          return (
            <g
              key={`${e.from}->${e.to}:${i}`}
              data-edge-style={e.style}
              data-edge-diff={tint}
              data-default-flow={e.isDefault || undefined}
            >
              <path d={g.d} fill="none" strokeWidth={1.5} strokeDasharray={dash} markerEnd={marker} className={cls} />
              {e.isDefault && g.horizontal && (
                // BPMN default-flow marker: a slash across the flow near its source.
                <path d={defaultSlashD(g.sx, g.sy)} strokeWidth={1.5} className={EDGE_CLASS[e.style]} />
              )}
              {e.label && <EdgeLabel layout={layout} edge={e} onBranchSelect={props.onBranchSelect} />}
              {ins && (
                // Edit mode: one "+" per gap on the sequence connector — inserting
                // "after the source step" is the same edit as "before the target".
                <ConnectorInsert x={g.mx} y={g.my} onSubmit={ins} />
              )}
            </g>
          );
        })}

        {/* Nodes. */}
        {layout.nodes.map((n) => (
          <Node
            key={n.id}
            n={n}
            props={props}
            onStepDragStart={canDragSteps ? startDrag : undefined}
            canReassign={canDragSteps ? props.edit?.canReassignStep : undefined}
            onReorderBodyDown={canDragEdit ? (nodeId, e) => drag.onBodyDown(String(nodeId), e) : undefined}
            reordering={drag.draggingNodeId === n.id}
            onPrompt={setStepPrompt}
          />
        ))}
        </g>
        </g>
      </svg>
    </div>
      {/* Sticky lane rail — a pinned copy of the rail chrome that stays at the
          left edge while the body scrolls horizontally, so a wide ribbon never
          loses its lane/pool labels. Additive overlay: the body rail is
          untouched; this is opaque (masks grid content sliding under it) and
          appears only once scrolled right. Vertical scroll is mirrored so
          bands stay aligned with their rows. Non-interactive (orientation
          only — editing stays on the body rail at x=0). */}
      {railW > 0 && scroll.left > 0 && (
        <svg
          width={railW}
          height="100%"
          className="pointer-events-none absolute left-0 top-0"
          aria-hidden="true"
          data-testid="bpmn-sticky-rail"
        >
          <rect x={0} y={0} width={railW} height="100%" className="fill-slate-950" />
          <g transform={`translate(0, ${headroom - scroll.top})`}>
            <LaneRail
              lanes={lanes}
              pools={layout.pools}
              spineW={spineW}
              labelW={labelW}
              totalW={railW}
              onSelectModule={props.onSelectModule}
            />
          </g>
        </svg>
      )}
      <ProcessMinimap
        contentW={w}
        contentH={h}
        cellW={CW}
        cellH={CH}
        pad={PAD}
        headroom={headroom}
        dots={layout.nodes.map((n) => ({
          x: n.x,
          y: n.y,
          round: n.kind === "event" || n.kind === "gateway",
        }))}
        scrollRef={scrollRef}
        scroll={scroll}
        viewW={viewport.w}
        viewH={viewport.h}
      />
      <StepEditPrompt spec={stepPrompt} onClose={() => setStepPrompt(null)} />
    </div>
  );
}

/** Swimlane rail — band separators (full width), pool borders + vertical
 *  spine titles, lane labels. Same cell metrics as the headless composer's
 *  `composeLaneRail`; paint stays Tailwind classes (live theme). When `edit`
 *  is present the rail also renders rename/remove/reorder/add affordances on
 *  editable bands (S-lane-edit) — a live-DOM-only layer (foreignObject inputs),
 *  never part of the export composer. */
function LaneRail({
  lanes,
  pools,
  spineW,
  labelW,
  totalW,
  onSelectModule,
  edit,
}: {
  lanes: readonly BpmnLaneBand[];
  pools: readonly BpmnPoolBand[];
  spineW: number;
  labelW: number;
  totalW: number;
  onSelectModule: (qualified: string) => void;
  edit?: BpmnLaneEditApi;
}) {
  // `edit:<kind>:<id>` while renaming a band; `add:lane` / `add:pool` while
  // authoring a fresh clause. One string keeps the rail's local UI state flat.
  const [ui, setUi] = useState<string | null>(null);
  const top = (y0: number) => PAD + y0 * CH;
  const bottom = (y1: number) => PAD + (y1 + 1) * CH;
  const railW = spineW + labelW;
  // May be empty when container editing is enabled with no lanes yet — only the
  // AddRow footer renders in that case.
  const first = lanes[0];
  const last = lanes[lanes.length - 1];
  // Element lanes carry the element path as their id — clickable; merged /
  // value / default lanes (and persona names that resolve to nothing
  // downstream) are inert.
  const clickTarget = (l: BpmnLaneBand): string | null =>
    l.id === "#default" || l.id.startsWith("title:") || l.id.startsWith("val:") ? null : l.id;
  const editableLanes = lanes.filter((l) => l.editable);
  return (
    <g data-lane-rail>
      {lanes.slice(1).map((l) => (
        <line
          key={`sep-${l.id}`}
          x1={0}
          y1={top(l.y0)}
          x2={totalW}
          y2={top(l.y0)}
          strokeWidth={LANE_SEPARATOR_STROKE}
          className="stroke-slate-700"
        />
      ))}
      {railW > 0 && first && last && (
        <line
          x1={railW}
          y1={top(first.y0)}
          x2={railW}
          y2={bottom(last.y1)}
          strokeWidth={LANE_SEPARATOR_STROKE}
          className="stroke-slate-700"
        />
      )}
      {labelW > 0 &&
        lanes.map((l) => {
          if (l.title === null) return null;
          const y0 = top(l.y0);
          const y1 = bottom(l.y1);
          const budget = Math.max(3 * LANE_LABEL_CHAR_H, y1 - y0 - LANE_LABEL_PAD);
          const cx = spineW + labelW / 2;
          const cy = (y0 + y1) / 2;
          const target = clickTarget(l);
          const canEdit = !!edit && !!l.editable;
          if (edit && ui === `edit:lane:${l.id}`) {
            return (
              <RenameBox
                key={l.id}
                x={spineW}
                y={y0}
                w={labelW}
                h={y1 - y0}
                initial={l.title}
                onCommit={(v) => {
                  edit.renameLane(l, v);
                  setUi(null);
                }}
                onCancel={() => setUi(null)}
              />
            );
          }
          return (
            <g key={l.id} data-lane={l.id} data-lane-editable={l.editable || undefined}>
              <title>{l.title}</title>
              <text
                x={cx}
                y={cy + LANE_LABEL_SIZE / 2 - 1}
                textAnchor="middle"
                transform={`rotate(-90 ${cx} ${cy})`}
                fontSize={LANE_LABEL_SIZE}
                onClick={target ? () => onSelectModule(target) : undefined}
                onDoubleClick={canEdit ? () => setUi(`edit:lane:${l.id}`) : undefined}
                className={`fill-slate-500 ${target ? "cursor-pointer hover:fill-slate-300" : ""}`}
              >
                {fit(l.title, budget, LANE_LABEL_SIZE)}
              </text>
              {canEdit && (
                <RailControls
                  x={spineW + labelW / 2}
                  y={y0 + 9}
                  onRemove={() => edit.removeLane(l)}
                  onUp={editableLanes[0] !== l ? () => edit.reorderLane(l, "up") : undefined}
                  onDown={editableLanes[editableLanes.length - 1] !== l ? () => edit.reorderLane(l, "down") : undefined}
                  testId={`lane-controls-${l.id}`}
                />
              )}
            </g>
          );
        })}
      {pools.map((p) => {
        const members = lanes.filter((l) => l.poolId === p.id);
        if (members.length === 0) return null;
        const y0 = top(Math.min(...members.map((l) => l.y0)));
        const y1 = bottom(Math.max(...members.map((l) => l.y1)));
        const budget = Math.max(3 * LANE_LABEL_CHAR_H, y1 - y0 - LANE_LABEL_PAD);
        const cx = spineW / 2;
        const cy = (y0 + y1) / 2;
        const canEdit = !!edit && !!p.editable;
        return (
          <g key={p.id} data-pool={p.id} data-pool-editable={p.editable || undefined}>
            <title>{p.title}</title>
            <rect
              x={POOL_BORDER_STROKE / 2}
              y={y0}
              width={totalW - POOL_BORDER_STROKE}
              height={y1 - y0}
              rx={POOL_BORDER_RADIUS}
              fill="none"
              strokeWidth={POOL_BORDER_STROKE}
              className="stroke-slate-600"
            />
            {edit && ui === `edit:pool:${p.id}` ? (
              <RenameBox
                x={0}
                y={y0}
                w={spineW}
                h={y1 - y0}
                initial={p.title}
                onCommit={(v) => {
                  edit.renamePool(p, v);
                  setUi(null);
                }}
                onCancel={() => setUi(null)}
              />
            ) : (
              <>
                <text
                  x={cx}
                  y={cy + LANE_LABEL_SIZE / 2 - 1}
                  textAnchor="middle"
                  transform={`rotate(-90 ${cx} ${cy})`}
                  fontSize={LANE_LABEL_SIZE}
                  fontWeight={500}
                  onDoubleClick={canEdit ? () => setUi(`edit:pool:${p.id}`) : undefined}
                  className="fill-slate-500"
                >
                  {fit(p.title, budget, LANE_LABEL_SIZE, FONT_WEIGHT_MEDIUM)}
                </text>
                {canEdit && (
                  <RailControls x={spineW / 2} y={y0 + 9} onRemove={() => edit.removePool(p)} testId={`pool-controls-${p.id}`} />
                )}
              </>
            )}
          </g>
        );
      })}
      {edit && !edit.reassignOnly && (
        <AddRow
          x={0}
          y={last ? bottom(last.y1) + 4 : PAD}
          w={Math.max(railW, LANE_RAIL_CELL_W)}
          mode={ui === "add:lane" ? "lane" : ui === "add:pool" ? "pool" : null}
          onOpen={(m) => setUi(`add:${m}`)}
          onCommit={(m, v) => {
            if (m === "lane") edit.addLane(v);
            else edit.addPool(v);
            setUi(null);
          }}
          onCancel={() => setUi(null)}
        />
      )}
    </g>
  );
}

/** Small SVG glyph buttons on a band: remove (✕) + optional reorder (▲▼).
 *  Faint until the band is hovered (`group-hover` on the parent `<g>`). */
function RailControls({
  x,
  y,
  onRemove,
  onUp,
  onDown,
  testId,
}: {
  x: number;
  y: number;
  onRemove: () => void;
  onUp?: () => void;
  onDown?: () => void;
  testId: string;
}) {
  const btn = "cursor-pointer fill-slate-500 hover:fill-slate-200 text-[11px]";
  return (
    <g data-testid={testId}>
      <text x={x} y={y} textAnchor="middle" className={btn} data-rail-remove onClick={(e) => (e.stopPropagation(), onRemove())}>
        ✕
      </text>
      {onUp && (
        <text x={x - 11} y={y} textAnchor="middle" className={btn} data-rail-up onClick={(e) => (e.stopPropagation(), onUp())}>
          ▲
        </text>
      )}
      {onDown && (
        <text x={x + 11} y={y} textAnchor="middle" className={btn} data-rail-down onClick={(e) => (e.stopPropagation(), onDown())}>
          ▼
        </text>
      )}
    </g>
  );
}

/** A foreignObject text input spanning a band's label column — the inline
 *  rename / add editor. Live-DOM only (never exported). */
function RenameBox({
  x,
  y,
  w,
  h,
  initial,
  onCommit,
  onCancel,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  initial: string;
  onCommit: (v: string) => void;
  onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  return (
    <foreignObject x={x} y={y} width={Math.max(w, 90)} height={Math.min(h, 26)}>
      <input
        autoFocus
        value={v}
        data-testid="rail-rename-input"
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit(v);
          else if (e.key === "Escape") onCancel();
        }}
        onBlur={() => onCommit(v)}
        className="w-full box-border rounded-sm border border-sky-500 bg-slate-900 px-1 text-[11px] text-slate-100 outline-none"
      />
    </foreignObject>
  );
}

/** The "+ lane / + pool" footer control below the last band. */
function AddRow({
  x,
  y,
  w,
  mode,
  onOpen,
  onCommit,
  onCancel,
}: {
  x: number;
  y: number;
  w: number;
  mode: "lane" | "pool" | null;
  onOpen: (m: "lane" | "pool") => void;
  onCommit: (m: "lane" | "pool", v: string) => void;
  onCancel: () => void;
}) {
  if (mode) {
    return (
      <RenameBox
        x={x}
        y={y}
        w={w}
        h={24}
        initial=""
        onCommit={(v) => (v.trim() ? onCommit(mode, v) : onCancel())}
        onCancel={onCancel}
      />
    );
  }
  return (
    <g data-testid="rail-add">
      <text x={x + 2} y={y + 12} className="cursor-pointer fill-slate-500 hover:fill-slate-200 text-[10px]" data-add-lane onClick={() => onOpen("lane")}>
        + lane
      </text>
      <text x={x + 40} y={y + 12} className="cursor-pointer fill-slate-500 hover:fill-slate-200 text-[10px]" data-add-pool onClick={() => onOpen("pool")}>
        + pool
      </text>
    </g>
  );
}

function EdgeLabel({
  layout,
  edge,
  onBranchSelect,
}: {
  layout: BpmnLayout;
  edge: BpmnEdge;
  onBranchSelect?: (nodeId: string, label: string) => void;
}) {
  const byId = new Map<string, BpmnNode>(layout.nodes.map((n) => [n.id, n]));
  const a = byId.get(edge.from);
  const b = byId.get(edge.to);
  if (!a || !b) return null;
  const x = (cx(a.x) + cx(b.x)) / 2;
  const y = (cy(a.y) + cy(b.y)) / 2 - 6;
  const pickable = !!edge.branchOf && !!onBranchSelect;
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      data-branch-pick={edge.branchOf ? `${edge.branchOf.frameId}:${edge.branchOf.label}` : undefined}
      onClick={
        pickable
          ? (ev) => {
              ev.stopPropagation();
              onBranchSelect!(edge.branchOf!.frameId, edge.branchOf!.label);
            }
          : undefined
      }
      className={`text-[10px] italic ${pickable ? "fill-sky-300 cursor-pointer underline" : "fill-slate-400"}`}
    >
      {fit(edge.label ?? "", LABEL_W_EDGE, FONT_LABEL)}
    </text>
  );
}

/** BPMN activity markers: loop ↻ / sequential MI (horizontal bars) /
 *  parallel MI (vertical bars) / subprocess ⊞. */
function ActivityMarker({
  x,
  y,
  marker,
}: {
  x: number;
  y: number;
  marker: "loop" | "multi" | "multi-parallel" | "subprocess";
}) {
  if (marker === "loop") {
    return (
      <g data-marker="loop">
        <path
          d={`M ${x - 5} ${y} a 5 5 0 1 0 2 -4.4`}
          fill="none"
          strokeWidth={1.4}
          className="stroke-slate-400"
        />
        <path d={`M ${x - 4.6} ${y - 5.8} l -1.8 2 l 2.6 0.8 z`} className="fill-slate-400" />
      </g>
    );
  }
  if (marker === "multi") {
    // Sequential multi-instance — three HORIZONTAL bars (BPMN 2.0).
    return (
      <g data-marker="multi">
        {[-4, 0, 4].map((dy) => (
          <line key={dy} x1={x - 5} y1={y + dy} x2={x + 5} y2={y + dy} strokeWidth={1.6} className="stroke-slate-400" />
        ))}
      </g>
    );
  }
  if (marker === "multi-parallel") {
    // Parallel multi-instance (fan-out) — three VERTICAL bars.
    return (
      <g data-marker="multi-parallel">
        {[-4, 0, 4].map((dx) => (
          <line key={dx} x1={x + dx} y1={y - 5} x2={x + dx} y2={y + 5} strokeWidth={1.6} className="stroke-slate-400" />
        ))}
      </g>
    );
  }
  return (
    <g data-marker="subprocess">
      <rect x={x - 6} y={y - 6} width={12} height={12} rx={1.5} fill="none" strokeWidth={1.3} className="stroke-slate-400" />
      <line x1={x - 3.5} y1={y} x2={x + 3.5} y2={y} strokeWidth={1.3} className="stroke-slate-400" />
      <line x1={x} y1={y - 3.5} x2={x} y2={y + 3.5} strokeWidth={1.3} className="stroke-slate-400" />
    </g>
  );
}

/** BPMN 2.0 gateway glyphs, drawn (not text): ×, +, ◯, ✳, event pentagon. */
function GatewayGlyph({ x, y, variant }: { x: number; y: number; variant: string }) {
  const cls = "stroke-slate-300";
  if (variant === "xor") {
    return (
      <g data-glyph="xor">
        <path d={`M ${x - 6} ${y - 6} L ${x + 6} ${y + 6} M ${x - 6} ${y + 6} L ${x + 6} ${y - 6}`} strokeWidth={2.4} className={cls} />
      </g>
    );
  }
  if (variant === "and") {
    return (
      <g data-glyph="and">
        <path d={`M ${x} ${y - 8} V ${y + 8} M ${x - 8} ${y} H ${x + 8}`} strokeWidth={2.4} className={cls} />
      </g>
    );
  }
  if (variant === "or") {
    return (
      <g data-glyph="or">
        <circle cx={x} cy={y} r={7} fill="none" strokeWidth={2.4} className={cls} />
      </g>
    );
  }
  if (variant === "complex") {
    return (
      <g data-glyph="complex">
        <path
          d={`M ${x} ${y - 8} V ${y + 8} M ${x - 8} ${y} H ${x + 8} M ${x - 5.7} ${y - 5.7} L ${x + 5.7} ${y + 5.7} M ${x - 5.7} ${y + 5.7} L ${x + 5.7} ${y - 5.7}`}
          strokeWidth={2}
          className={cls}
        />
      </g>
    );
  }
  // Event-based: double circle + pentagon.
  const p = 5.2;
  const pts = [
    [0, -p],
    [p * 0.951, -p * 0.309],
    [p * 0.588, p * 0.809],
    [-p * 0.588, p * 0.809],
    [-p * 0.951, -p * 0.309],
  ]
    .map(([dx, dy]) => `${x + dx},${y + dy}`)
    .join(" ");
  return (
    <g data-glyph="event">
      <circle cx={x} cy={y} r={10} fill="none" strokeWidth={1.2} className={cls} />
      <circle cx={x} cy={y} r={8} fill="none" strokeWidth={1.2} className={cls} />
      <polygon points={pts} fill="none" strokeWidth={1.2} className={cls} />
    </g>
  );
}

/** Event trigger/result glyphs, drawn to BPMN convention. */
function EventGlyph({ x, y, node }: { x: number; y: number; node: Extract<BpmnNode, { kind: "event" }> }) {
  const cls = "stroke-slate-300";
  if (node.event === "end-error" || node.event === "boundary-error") {
    // Lightning bolt.
    return (
      <path
        data-glyph="error"
        d={`M ${x + 2.5} ${y - 7} L ${x - 4.5} ${y + 1.5} H ${x - 1} L ${x - 2.5} ${y + 7} L ${x + 4.5} ${y - 1.5} H ${x + 1} Z`}
        className="fill-slate-300"
      />
    );
  }
  if (node.event === "end-cancel") {
    return (
      <path
        data-glyph="cancel"
        d={`M ${x - 6} ${y - 6} L ${x + 6} ${y + 6} M ${x - 6} ${y + 6} L ${x + 6} ${y - 6}`}
        strokeWidth={2.6}
        className={cls}
      />
    );
  }
  if (node.event === "link") {
    return (
      <path
        data-glyph="link"
        d={`M ${x - 6} ${y - 3} H ${x + 1} V ${y - 6} L ${x + 7} ${y} L ${x + 1} ${y + 6} V ${y + 3} H ${x - 6} Z`}
        fill="none"
        strokeWidth={1.4}
        className={cls}
      />
    );
  }
  if (node.trigger === "timer") {
    // Clock: circle + hands.
    return (
      <g data-glyph="timer">
        <circle cx={x} cy={y} r={7} fill="none" strokeWidth={1.3} className={cls} />
        <path d={`M ${x} ${y - 4.5} V ${y} L ${x + 3.5} ${y + 2}`} fill="none" strokeWidth={1.3} className={cls} />
      </g>
    );
  }
  if (node.trigger === "conditional") {
    // Conditional: lined page.
    return (
      <g data-glyph="conditional">
        <rect x={x - 5} y={y - 6.5} width={10} height={13} fill="none" strokeWidth={1.2} className={cls} />
        {[-3.5, -1, 1.5, 4].map((dy) => (
          <line key={dy} x1={x - 3} y1={y + dy} x2={x + 3} y2={y + dy} strokeWidth={1} className={cls} />
        ))}
      </g>
    );
  }
  return null;
}

type DanglingEngine = NonNullable<Extract<BpmnNode, { kind: "task" }>["danglingEngine"]>;

/** `engine @@<key>` — a small caller-derived aspect chip dangling off the task's
 *  top-right corner (mirrors the scene `composeEngineBadge`; geometry shared via
 *  ENGINE_* metrics). `data-logical-id` fans selection/hover to the one logical
 *  place behind the copies, parallel to the `system` cylinder. */
function EngineBadge({
  x,
  y,
  eng,
  accent,
}: {
  x: number;
  y: number;
  eng: DanglingEngine;
  accent: ReturnType<typeof diffAccent>;
}) {
  const left = x + TASK_W / 2 + ENGINE_BADGE_DX - ENGINE_BADGE_W;
  const top = y - TASK_H / 2 - ENGINE_BADGE_DY - ENGINE_BADGE_H;
  const bx = left + ENGINE_BADGE_W / 2;
  const stroke = accent.stroke ? undefined : eng.color;
  return (
    <g
      data-node-kind="engine"
      data-logical-id={eng.logicalId}
      data-aspect-value={eng.value}
    >
      <title>{eng.value}</title>
      <line
        x1={bx}
        y1={y - TASK_H / 2}
        x2={bx}
        y2={top + ENGINE_BADGE_H}
        strokeWidth={ENGINE_STROKE}
        style={{ stroke }}
        className={accent.stroke ?? undefined}
      />
      <rect
        x={left}
        y={top}
        width={ENGINE_BADGE_W}
        height={ENGINE_BADGE_H}
        rx={ENGINE_BADGE_R}
        strokeWidth={accent.stroke ? 2 : ENGINE_STROKE}
        strokeDasharray={accent.dash}
        style={{ stroke, fill: eng.color, fillOpacity: ENGINE_FILL_ALPHA }}
        className={accent.stroke ?? undefined}
      />
      <text
        x={bx}
        y={top + ENGINE_BADGE_H / 2 + 3}
        textAnchor="middle"
        className="text-[9px]"
        style={{ fill: eng.color }}
      >
        {fit(eng.value, LABEL_W_ENGINE, FONT_SMALL)}
      </text>
    </g>
  );
}

/** The live-conformance violation ring hue — fuchsia, byte-identical to the
 *  flow/sequence swimlane cell marker (swimlaneCells) so the whole overlay reads
 *  as one language across every process surface + the board. */
const REORDER_HUE = "#e879f9";

/** Additive violation ring for a task rect — fuchsia outline + ⇄, composited over
 *  the fill (survives active). `pointerEvents="none"` keeps clicks on the node. */
function ReorderRingRect({ x, y, w, h, rx }: { x: number; y: number; w: number; h: number; rx: number }) {
  return (
    <g data-reorder-marker pointerEvents="none">
      <rect x={x} y={y} width={w} height={h} rx={rx} fill="none" stroke={REORDER_HUE} strokeWidth={2} />
      <text x={x + 6} y={y + 12} fill={REORDER_HUE} className="text-[11px] font-semibold">
        ⇄
      </text>
    </g>
  );
}

/** Additive violation ring for an event circle — fuchsia ring + ⇄ above it. */
function ReorderRingCircle({ cx, cy, r }: { cx: number; cy: number; r: number }) {
  return (
    <g data-reorder-marker pointerEvents="none">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={REORDER_HUE} strokeWidth={2} />
      <text x={cx} y={cy - r - 3} textAnchor="middle" fill={REORDER_HUE} className="text-[11px] font-semibold">
        ⇄
      </text>
    </g>
  );
}

function Node({
  n,
  props,
  onStepDragStart,
  canReassign,
  onReorderBodyDown,
  reordering,
  onPrompt,
}: {
  n: BpmnNode;
  props: BpmnViewProps;
  onStepDragStart?: (stepIndex: number, e: ReactPointerEvent) => void;
  /** Present when step→lane editing is armed — whether THIS step's owner can be
   *  reassigned (a plain call with a rewritable owner span). A real step that is
   *  not reassignable shows a disabled drop + tooltip rather than a silent no-op. */
  canReassign?: (stepIndex: number) => boolean;
  /** Threshold-gated body drag for horizontal REORDER (edit mode). Present ⇒ a
   *  real call task grabs by its body to reorder along the sequence; a
   *  sub-threshold press stays a plain select click (useStepDrag owns the gate).
   *  Falls back to the lane-reassign pointerdown when that is armed for the node. */
  onReorderBodyDown?: (nodeId: string, e: ReactPointerEvent) => void;
  /** This node is the one under an active reorder drag (grab cursor + dim). */
  reordering?: boolean;
  /** Open the surface's floating text prompt (right-click edit ops). */
  onPrompt?: (spec: StepPromptSpec) => void;
}) {
  const menu = useContextMenu();
  const active =
    "stepIndex" in n && n.stepIndex !== null && n.stepIndex !== undefined && n.stepIndex === props.activeStepIndex;
  // Live conformance: this step ran out of order in ≥1 observed trace. The ring
  // is additive (composited over the fill), so it survives the active highlight.
  const reordered =
    "stepIndex" in n && n.stepIndex !== null && n.stepIndex !== undefined && !!props.reorderedSteps?.has(n.stepIndex);
  const x = cx(n.x);
  const y = cy(n.y);
  const activeCls = active ? "stroke-sky-400" : "";

  if (n.kind === "task") {
    const isSubprocess = !!n.subprocess;
    const accent = diffAccent(n.diff);
    const edgeStyle =
      n.stepIndex !== null && n.stepIndex !== undefined ? props.resolveStepEdgeStyle?.(n.stepIndex) : undefined;
    // Owner colour ("colour by" — type/label) applies only when no stronger
    // accent claims the stroke, and never to actors (they own no module). The
    // per-step interface colour (parity with flow/sequence) is a fallback source
    // when there's no owner tint.
    const oc =
      !accent.stroke && !n.compensation && !active && n.owner && !n.owner.isActor
        ? props.ownerColor?.(n.owner.path) ?? (!edgeStyle?.compensation ? edgeStyle?.color : undefined)
        : undefined;
    const latency = n.stepIndex !== null && n.stepIndex !== undefined ? props.stepLatencies?.get(n.stepIndex) : undefined;
    // Edit-mode click semantics: single-click SELECTS (never navigates), so a
    // dive (expand a subprocess) moves to double-click. Viewing keeps the direct
    // single-click dive. `editing` = the step-edit api is wired for this surface.
    const editing = !!props.stepEdit;
    const onClick = () => {
      if (isSubprocess && !editing) props.onSubprocessToggle(n.subprocess!.nodeId, true);
      else if (n.stepIndex !== null && n.stepIndex !== undefined) props.onSelectStepIndex(n.stepIndex);
    };
    const onDoubleClick =
      isSubprocess && editing ? () => props.onSubprocessToggle(n.subprocess!.nodeId, true) : undefined;
    const navItems: ContextMenuItem[] = [
      { label: isSubprocess ? "Expand" : "Select step", onClick },
      { label: "Copy step id", onClick: () => void navigator.clipboard?.writeText(n.id), separator: true },
      { label: "Copy label", onClick: () => void navigator.clipboard?.writeText(n.label) },
    ];
    // Edit mode: fold the step-edit ops into the SAME right-click menu (at the
    // pointer, text ops open the surface prompt). The ⋯ trigger stays for
    // discoverability; both drive the one ProcessStepEditApi.
    const openMenu = (e: ReactPointerEvent | React.MouseEvent) => {
      const editItems =
        props.stepEdit && n.stepIndex !== null && n.stepIndex !== undefined && onPrompt
          ? stepEditMenuItems(
              props.stepEdit,
              {
                nodeId: n.id,
                kind: isSubprocess ? "other" : "call",
                stepIndex: n.stepIndex,
                from: n.owner && !n.owner.isActor ? n.owner.path.join(".") : undefined,
              },
              (spec) => onPrompt({ ...spec, x: e.clientX, y: e.clientY }),
            )
          : [];
      menu.open(e, [...navItems, ...editItems]);
    };
    // A real call step (not a collapsed `do`) can be dragged to another lane —
    // an OWNER model-edit resolved on drop (see BpmnView.finishDrag). Editing is
    // "armed" when `canReassign` is supplied; a step whose owner isn't
    // source-rewritable (compound/bind/args/comment/instance, implicit-caller
    // chain hop) shows a DISABLED drop + tooltip instead of silently no-op'ing.
    const editArmed = !!canReassign && !!onStepDragStart && !isSubprocess && n.stepIndex !== null;
    const draggable = editArmed && canReassign!(n.stepIndex!);
    const dragDisabled = editArmed && !draggable;
    // Horizontal REORDER body-drag (P5 #35): a real call task, when not already
    // claimed by the vertical lane-reassign grip. Threshold-gated by useStepDrag,
    // so a plain click still selects. Lane-reassign (a laned view) wins the
    // pointerdown when it is armed for this node — the two never both fire.
    const reorderable = !!onReorderBodyDown && !isSubprocess && !n.compensation && n.stepIndex !== null && n.stepIndex !== undefined && !draggable;
    const onPointerDown = draggable
      ? (e: ReactPointerEvent) => onStepDragStart!(n.stepIndex!, e)
      : reorderable
        ? (e: ReactPointerEvent) => onReorderBodyDown!(n.id, e)
        : undefined;
    return (
      <g
        data-node-id={n.id}
        data-node-kind={isSubprocess ? "subprocess" : "task"}
        data-step-index={n.stepIndex ?? undefined}
        data-active={active || undefined}
        data-reordered={reordered || undefined}
        data-dimmed={n.dimmed || undefined}
        data-diff={n.diff?.status}
        data-owner-colored={oc ? true : undefined}
        data-step-draggable={draggable || undefined}
        data-step-reorderable={reorderable || undefined}
        data-reordering={reordering || undefined}
        data-step-drag-disabled={dragDisabled || undefined}
        opacity={accent.ghost ? 0.55 : reordering ? 0.5 : n.dimmed ? 0.45 : undefined}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={openMenu}
        onPointerDown={onPointerDown}
        className={draggable ? "cursor-grab" : dragDisabled ? "cursor-not-allowed" : reorderable ? "cursor-grab" : "cursor-pointer"}
      >
        <rect
          x={x - TASK_W / 2}
          y={y - TASK_H / 2}
          width={TASK_W}
          height={TASK_H}
          rx={8}
          strokeWidth={active || accent.stroke || oc ? 2.5 : 1.5}
          strokeDasharray={accent.dash}
          style={oc ? { stroke: oc } : undefined}
          className={`${n.compensation ? "fill-amber-950/60" : "fill-slate-900"} ${
            accent.stroke ?? (n.compensation ? "stroke-amber-500/60" : "stroke-slate-500")
          } ${activeCls}`}
        />
        {reordered && <ReorderRingRect x={x - TASK_W / 2} y={y - TASK_H / 2} w={TASK_W} h={TASK_H} rx={8} />}
        <DiffChip x={x} y={y + TASK_H / 2 + 12} diff={n.diff} />
        <title>{dragDisabled ? `${n.label} — this step's lane can't be reassigned by drag` : n.label}</title>
        <text x={x} y={y + 4} textAnchor="middle" className="fill-slate-200 text-[11px]">
          {fit(n.label, LABEL_W_TASK, FONT_TITLE)}
        </text>
        {n.owner && (
          <text
            x={x - TASK_W / 2}
            y={y - TASK_H / 2 - 5}
            style={oc ? { fill: oc } : undefined}
            className={`text-[10px] cursor-pointer ${n.owner.isActor ? "fill-emerald-300 italic" : "fill-slate-400"}`}
            data-owner-chip
            onClick={(ev) => {
              ev.stopPropagation();
              props.onSelectModule(n.owner!.path.join("."));
            }}
          >
            {fit(n.owner.label, LABEL_W_OWNER, FONT_LABEL)}
          </text>
        )}
        {n.inbound && (
          // Hollow envelope — BPMN message catch.
          <g data-inbound>
            <rect x={x + TASK_W / 2 - 20} y={y - TASK_H / 2 + 6} width={13} height={9} fill="none" strokeWidth={1.1} className="stroke-sky-300" />
            <path
              d={`M ${x + TASK_W / 2 - 20} ${y - TASK_H / 2 + 6} l 6.5 5 l 6.5 -5`}
              fill="none"
              strokeWidth={1.1}
              className="stroke-sky-300"
            />
          </g>
        )}
        {isSubprocess && <ActivityMarker x={x} y={y + TASK_H / 2 - 9} marker="subprocess" />}
        {n.compensation && (
          // Compensation marker: double rewind triangles.
          <g data-compensation>
            <path
              d={`M ${x - 1} ${y - TASK_H / 2 + 12} l -5 -3.5 v 7 z M ${x + 5} ${y - TASK_H / 2 + 12} l -5 -3.5 v 7 z`}
              className="fill-amber-400"
            />
          </g>
        )}
        {n.danglingEngine && <EngineBadge x={x} y={y} eng={n.danglingEngine} accent={accent} />}
        {latency?.label && latency.label !== "???" && (
          <text
            x={x + TASK_W / 2 - 2}
            y={y + TASK_H / 2 + 11}
            textAnchor="end"
            className="fill-slate-400 text-[9px] tabular-nums"
            data-latency-badge
          >
            {latency.label}
          </text>
        )}
        {props.stepEdit && n.stepIndex !== null && (
          <foreignObject x={x + TASK_W / 2 - 16} y={y - TASK_H / 2 + 2} width={18} height={18} style={{ overflow: "visible" }}>
            <StepEditMenu
              api={props.stepEdit}
              nodeId={n.id}
              kind={isSubprocess ? "other" : "call"}
              stepIndex={n.stepIndex ?? undefined}
              from={n.owner && !n.owner.isActor ? n.owner.path.join(".") : undefined}
            />
          </foreignObject>
        )}
        {menu.node}
      </g>
    );
  }

  if (n.kind === "gateway") {
    const accent = diffAccent(n.diff);
    const oc =
      !accent.stroke && n.owner && !n.owner.isActor ? props.ownerColor?.(n.owner.path) : undefined;
    return (
      <g
        data-node-id={n.id}
        data-node-kind="gateway"
        data-gateway={`${n.gate}:${n.variant}`}
        data-diff={n.diff?.status}
        data-owner-colored={oc ? true : undefined}
        opacity={accent.ghost ? 0.55 : undefined}
      >
        <polygon
          points={`${x},${y - GATE_R} ${x + GATE_R},${y} ${x},${y + GATE_R} ${x - GATE_R},${y}`}
          strokeWidth={accent.stroke || oc ? 2.5 : 1.5}
          strokeDasharray={accent.dash}
          style={oc ? { stroke: oc } : undefined}
          className={`fill-slate-900 ${accent.stroke ?? "stroke-slate-500"}`}
        />
        <GatewayGlyph x={x} y={y} variant={n.variant} />
        {n.label && (
          <text x={x} y={y - GATE_R - 6} textAnchor="middle" className="fill-slate-400 text-[10px] italic">
            {fit(n.label, LABEL_W_GATEWAY, FONT_LABEL)}
          </text>
        )}
      </g>
    );
  }

  if (n.kind === "event") {
    const thick = n.event.startsWith("end");
    const doubleRing = n.event === "intermediate" || n.event === "link" || n.event === "boundary-error";
    const clickable = n.stepIndex !== undefined;
    const accent = diffAccent(n.diff);
    const oc =
      !accent.stroke && !n.compensation && !active && n.owner && !n.owner.isActor
        ? props.ownerColor?.(n.owner.path)
        : undefined;
    const eventMenu: ContextMenuItem[] = clickable
      ? [
          { label: "Select step", onClick: () => props.onSelectStepIndex(n.stepIndex!) },
          { label: "Copy step id", onClick: () => void navigator.clipboard?.writeText(n.id), separator: true },
          ...(n.label
            ? [{ label: "Copy label", onClick: () => void navigator.clipboard?.writeText(n.label!) }]
            : []),
        ]
      : [];
    return (
      <g
        data-node-id={n.id}
        data-node-kind="event"
        data-event={n.event}
        data-trigger={n.trigger}
        data-step-index={n.stepIndex}
        data-active={active || undefined}
        data-reordered={reordered || undefined}
        data-dimmed={n.dimmed || undefined}
        data-diff={n.diff?.status}
        data-owner-colored={oc ? true : undefined}
        opacity={accent.ghost ? 0.55 : n.dimmed ? 0.45 : undefined}
        onClick={clickable ? () => props.onSelectStepIndex(n.stepIndex!) : undefined}
        onContextMenu={clickable ? (e) => menu.open(e, eventMenu) : undefined}
        className={clickable ? "cursor-pointer" : undefined}
      >
        <circle
          cx={x}
          cy={y}
          r={EVENT_R}
          strokeWidth={active ? 3 : thick ? 3 : oc ? 2 : 1.5}
          strokeDasharray={accent.dash}
          style={oc ? { stroke: oc } : undefined}
          className={`fill-slate-900 ${accent.stroke ?? (n.compensation ? "stroke-amber-500/70" : "stroke-slate-400")} ${activeCls}`}
        />
        {doubleRing && <circle cx={x} cy={y} r={EVENT_R - 4} fill="none" strokeWidth={1} className="stroke-slate-400" />}
        {n.event === "end-terminate" && <circle cx={x} cy={y} r={EVENT_R - 7} className="fill-slate-400" />}
        {reordered && <ReorderRingCircle cx={x} cy={y} r={EVENT_R + 2} />}
        <EventGlyph x={x} y={y} node={n} />
        {n.label && (
          <>
            <title>{n.label}</title>
            <text x={x} y={y + EVENT_R + 13} textAnchor="middle" className="fill-slate-400 text-[10px]">
              {fit(n.label, LABEL_W_EVENT, FONT_LABEL)}
            </text>
          </>
        )}
        {menu.node}
      </g>
    );
  }

  if (n.kind === "system") {
    // A `replicate`d aspect place — a data-store cylinder half a track below its
    // task, tinted by the aspect value (mirrors the scene's composeSystem; board
    // `web`/`replicated` and process agree). `data-logical-id` fans selection out
    // to the one logical node behind the copies.
    const accent = diffAccent(n.diff);
    const stroke = accent.stroke ? undefined : n.accent;
    const w = SYSTEM_W;
    const h = SYSTEM_H;
    const rx = w / 2;
    const ry = SYSTEM_CAP_RY;
    const left = x - rx;
    const right = x + rx;
    const capY = y - h / 2 + ry;
    const baseY = y + h / 2 - ry;
    return (
      <g
        data-node-id={n.id}
        data-node-kind="system"
        data-logical-id={n.logicalId}
        data-aspect-value={n.accentValue}
        data-dimmed={n.dimmed || undefined}
        data-diff={n.diff?.status}
        opacity={accent.ghost ? 0.55 : n.dimmed ? 0.45 : undefined}
      >
        <title>{n.label}</title>
        <path
          d={`M ${left} ${capY} V ${baseY} A ${rx} ${ry} 0 0 0 ${right} ${baseY} V ${capY}`}
          strokeWidth={accent.stroke ? 2 : SYSTEM_STROKE}
          strokeDasharray={accent.dash}
          style={{ stroke, fill: n.accent, fillOpacity: SYSTEM_FILL_ALPHA }}
          className={accent.stroke ?? undefined}
        />
        <path
          d={`M ${left} ${capY} A ${rx} ${ry} 0 0 0 ${right} ${capY} A ${rx} ${ry} 0 0 0 ${left} ${capY} Z`}
          strokeWidth={accent.stroke ? 2 : SYSTEM_STROKE}
          strokeDasharray={accent.dash}
          style={{ stroke, fill: n.accent, fillOpacity: SYSTEM_FILL_ALPHA }}
          className={accent.stroke ?? undefined}
        />
        <text x={x} y={y + h / 2 + SYSTEM_LABEL_DY} textAnchor="middle" className="fill-slate-400 text-[10px]">
          {fit(n.label, LABEL_W_SYSTEM, FONT_SMALL)}
        </text>
      </g>
    );
  }

  // Text annotation — BPMN open bracket, not a closed box. Owner-anchored
  // notes colour like any owned element; ownerless prose stays neutral.
  const ax = x - TASK_W / 2;
  const accent = diffAccent(n.diff);
  const oc =
    !accent.stroke && !active && n.owner && !n.owner.isActor
      ? props.ownerColor?.(n.owner.path)
      : undefined;
  return (
    <g
      data-node-id={n.id}
      data-node-kind="annotation"
      data-step-index={n.stepIndex}
      data-active={active || undefined}
      data-dimmed={n.dimmed || undefined}
      data-diff={n.diff?.status}
      data-owner-colored={oc ? true : undefined}
      opacity={accent.ghost ? 0.55 : n.dimmed ? 0.45 : undefined}
    >
      <path
        d={`M ${ax + 12} ${y - TASK_H / 2 + 8} H ${ax} V ${y + TASK_H / 2 - 8} H ${ax + 12}`}
        fill="none"
        strokeWidth={active || oc ? 2 : 1.2}
        style={oc ? { stroke: oc } : undefined}
        className={`${accent.stroke ?? "stroke-slate-500"} ${activeCls}`}
      />
      <title>{n.text}</title>
      <text x={ax + 8} y={y + 3} className="fill-slate-400 text-[10px] italic">
        {fit(n.text, LABEL_W_ANNOTATION, FONT_LABEL)}
      </text>
      {n.owner && (
        <text
          x={ax}
          y={y - TASK_H / 2 + 2}
          style={oc ? { fill: oc } : undefined}
          className="fill-slate-500 text-[9px]"
        >
          {fit(n.owner.label, LABEL_W_OWNER, FONT_SMALL)}
        </text>
      )}
    </g>
  );
}
